import { useState, useEffect, useMemo, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { getApiBase, getConnectionHint } from "@/apiBase";
import { buildMonthBirthdays } from "@/lib/monthBirthdays";
import {
  CELL_OPTIONAL_PARENT_LINK_LABEL,
  CELL_MEETING_DAY_OPTIONS,
  CELL_MEETING_MODALITY_OPTIONS,
} from "@/lib/cellFormLabels";
import { getCellMongoId, logCellApiDebug, normalizeCellFromApi } from "@/lib/cellIds";
import { formatMentor, normalizeMemberFromApi } from "@/lib/memberMentorForm";
import { api } from "@/lib/apiClient";
import {
  ROLE_ADMIN,
  ROLE_LIDER,
  ROLE_PASTOR,
  ROLE_SECRETARIO,
  ROLE_SUPERVISOR,
  ROLES_REQUIRING_MEMBER_ID,
  canAccessGlobalTrash,
  canAccessAdminTools,
  canManageOrg,
  canPermanentDeleteFromTrash,
  canRestoreFromTrash,
  canViewCellsTrash,
  canWriteMembers,
  clearAdminToolsUnlock,
  isCellStaff,
  isFullAccess,
  isPastorOrSupervisor,
  isPastorSupervisorOrCellStaff,
  isSupervisor,
  roleLabel,
} from "@/lib/roles";
import { MembersPanel } from "@/components/MembersPanel";
import { AdminToolsPanel } from "@/components/AdminToolsPanel";
import { DashboardCellsOverview } from "@/components/DashboardCellsOverview";
import { NativeDialog, NativeSelect, NativeSwitch, NativeTabs } from "@/components/nativeUi";
import { DataIoToolbar } from "@/components/DataIoToolbar";
import { CellOrganizationChart } from "@/components/CellOrganizationChart";
import { CellMeetingAttendanceScreen } from "@/components/CellMeetingAttendance";
import { CellQuickWeeklyStatsScreen } from "@/components/CellQuickWeeklyStats";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Users, Home, LogOut, Plus, Edit, Trash2, Eye, 
  UserPlus, Building2, BarChart3, User, Lock,
  Phone, Calendar, MapPin, Heart, School, Gift, Cake, X,
  FileText, ArchiveRestore, Shield, UserCog, CalendarCheck, TrendingUp, Wrench
} from "lucide-react";

const API_BASE = getApiBase();

const formatApiError = (err) => {
  const data = err.response?.data;
  if (data && typeof data === "object" && data.service === "ICJEME API" && data.api === "/api" && !data.detail) {
    return "La petición llegó a la raíz del servidor (/) en lugar de /api/.... Revise REACT_APP_BACKEND_URL y el proxy: la URL del backend no debe terminar en /api duplicado; use la app en el puerto del React (p. ej. :3001).";
  }
  const d = data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d
      .map((x) => {
        if (typeof x === "string") return x;
        const loc = Array.isArray(x.loc) ? x.loc.filter(Boolean).join(".") : "";
        const msg = x.msg || x.message || (typeof x === "object" ? JSON.stringify(x) : String(x));
        return loc ? `${loc}: ${msg}` : msg;
      })
      .filter(Boolean)
      .join(" · ");
  }
  if (d && typeof d === "object" && typeof d.msg === "string") return d.msg;
  if (!err.response) {
    if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
      return `No hay conexión con el servidor (${getConnectionHint()}). Compruebe que el backend esté en marcha y que REACT_APP_BACKEND_URL en frontend/.env apunte a la base del API sin /api (por ejemplo http://127.0.0.1:8000). Tras cambiar .env, reinicie npm start.`;
    }
    return err.message || "Sin respuesta del servidor.";
  }
  const st = err.response.status;
  const txt = err.response.statusText;
  return st ? `Error ${st}${txt ? ` (${txt})` : ""}` : "No se pudo completar la operación.";
};

/** Promedio de asistencia semanal en dashboard (entero o un decimal). */
function formatWeeklyAttendanceAvgDisplay(v) {
  if (v == null || v === "") return "0";
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return "0";
  const rounded = Math.round(n * 10) / 10;
  if (Math.abs(rounded - Math.round(rounded)) < 1e-9) return String(Math.round(rounded));
  return rounded.toFixed(1);
}

/** Etiquetas del campo member_id en registro (estructura estable, sin JSX condicional). */
const REG_MEMBER_ID_LABEL = {
  [ROLE_PASTOR]: "ID del Pastor o la Pastora",
  [ROLE_ADMIN]: "ID del Pastor o la Pastora",
  [ROLE_SUPERVISOR]: "ID del miembro (líder de célula en el sistema)",
  [ROLE_LIDER]: "ID del miembro (su propio member_id)",
  [ROLE_SECRETARIO]: "ID del miembro (su propio member_id)",
};

const REG_MEMBER_ID_HELP = {
  [ROLE_PASTOR]:
    "Solo el ID del miembro registrado como Pastor o Pastora en Miembros. No use el ID de líderes, supervisores u otros cargos.",
  [ROLE_ADMIN]:
    "Solo el ID del miembro registrado como Pastor o Pastora en Miembros. El administrador tiene los mismos permisos que el pastor.",
  [ROLE_SUPERVISOR]:
    "ID de registro del miembro en Miembros. Debe ser líder (leader_id) de una célula activa.",
  [ROLE_LIDER]:
    "Su propio ID de registro. El miembro debe tener una célula asignada en su ficha.",
  [ROLE_SECRETARIO]:
    "Su propio ID de registro. El miembro debe tener una célula asignada (mismos permisos que líder de célula).",
};

// ============ LOGIN COMPONENT ============
const Login = ({ onLogin }) => {
  const [mode, setMode] = useState("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regRole, setRegRole] = useState("pastor");
  const [regMemberId, setRegMemberId] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const regMemberIdLabel = REG_MEMBER_ID_LABEL[regRole] ?? REG_MEMBER_ID_LABEL.pastor;
  const regMemberIdHelp = REG_MEMBER_ID_HELP[regRole] ?? REG_MEMBER_ID_HELP.pastor;

  const handleRegRoleChange = useCallback((role) => {
    setRegRole(role);
    setRegMemberId("");
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    if (!API_BASE) {
      setError(
        "Falta REACT_APP_BACKEND_URL. En frontend/.env agregue, por ejemplo: REACT_APP_BACKEND_URL=http://127.0.0.1:8000 (sin /api; ajuste el puerto) y ejecute de nuevo npm start.",
      );
      setLoading(false);
      return;
    }
    try {
      const response = await api.post("/auth/login", { username, password });
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      onLogin(response.data.user);
    } catch (err) {
      setError(formatApiError(err) || "Credenciales inválidas");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    if (!API_BASE) {
      setError(
        "Falta REACT_APP_BACKEND_URL. En frontend/.env agregue, por ejemplo: REACT_APP_BACKEND_URL=http://127.0.0.1:8000 (sin /api; ajuste el puerto) y ejecute de nuevo npm start.",
      );
      setLoading(false);
      return;
    }
    const body = {
      username: username.trim(),
      password,
      name: regName.trim(),
      role: regRole,
      email: regEmail.trim() ? regEmail.trim() : undefined,
    };
    if (ROLES_REQUIRING_MEMBER_ID.includes(regRole)) {
      body.member_id = regMemberId.trim();
      if (!body.member_id) {
        setError("Indique el ID del miembro (member_id) para el rol seleccionado.");
        setLoading(false);
        return;
      }
    }
    if (!body.username || !body.password || !body.name) {
      setError("Complete usuario, contraseña y nombre.");
      setLoading(false);
      return;
    }
    try {
      await api.post("/auth/register", body);
    } catch (regErr) {
      setError(formatApiError(regErr));
      setLoading(false);
      return;
    }
    try {
      const response = await api.post("/auth/login", { username: body.username, password: body.password });
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      onLogin(response.data.user);
    } catch (logErr) {
      setError(`Cuenta creada, pero no se pudo iniciar sesión: ${formatApiError(logErr)}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100">
      <Card className="w-full max-w-md mx-4 shadow-xl">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center">
            <Building2 className="w-10 h-10 text-white" />
          </div>
          <CardTitle className="text-3xl font-bold text-blue-900">ICJEME</CardTitle>
          <CardDescription className="text-lg">Sistema de Gestión de Células</CardDescription>
          {!API_BASE && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3 text-left leading-relaxed">
              Falta la variable{" "}
              <code className="text-xs bg-white px-1 rounded">REACT_APP_BACKEND_URL</code>
              {" "}en el frontend. Cree el archivo{" "}
              <code className="text-xs bg-white px-1 rounded">frontend/.env</code>
              {" "}con una línea como:{" "}
              <code className="text-xs break-all block mt-1 mb-1 bg-white px-1 py-0.5 rounded">
                REACT_APP_BACKEND_URL=http://127.0.0.1:8000
              </code>
              Luego reinicie el servidor de desarrollo:{" "}
              <code className="text-xs bg-white px-1 rounded">npm start</code>.
            </p>
          )}
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <Button type="button" variant={mode === "login" ? "default" : "outline"} className="flex-1" onClick={() => setMode("login")}>Entrar</Button>
            <Button type="button" variant={mode === "register" ? "default" : "outline"} className="flex-1" onClick={() => setMode("register")}>Registro</Button>
          </div>
          <form
            onSubmit={handleLogin}
            className={`space-y-4 ${mode === "login" ? "" : "hidden"}`}
            aria-hidden={mode !== "login"}
          >
              <div className="space-y-2">
                <Label htmlFor="username">Usuario</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="username"
                    type="text"
                    placeholder="Usuario"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="pl-10"
                    data-testid="login-username"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Contraseña"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    data-testid="login-password"
                  />
                </div>
              </div>
              {error && (
                <div className="text-red-500 text-sm text-center bg-red-50 p-2 rounded">
                  {error}
                </div>
              )}
              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700" 
                disabled={loading}
                data-testid="login-button"
              >
                {loading ? "Iniciando..." : "Iniciar Sesión"}
              </Button>
          </form>
          <form
            onSubmit={handleRegister}
            className={`space-y-4 notranslate ${mode === "register" ? "" : "hidden"}`}
            aria-hidden={mode !== "register"}
            translate="no"
            lang="es"
          >
              <div className="space-y-2">
                <Label>Nombre completo</Label>
                <Input value={regName} onChange={(e) => setRegName(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label>Usuario</Label>
                <Input value={username} onChange={(e) => setUsername(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label>Contraseña</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label>Correo (opcional)</Label>
                <Input type="email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reg-role">Rol</Label>
                <NativeSelect id="reg-role" value={regRole} onChange={handleRegRoleChange}>
                  <option value={ROLE_PASTOR}>Pastor</option>
                  <option value={ROLE_ADMIN}>Administrador</option>
                  <option value={ROLE_SUPERVISOR}>Supervisor</option>
                  <option value={ROLE_LIDER}>Líder de célula</option>
                  <option value={ROLE_SECRETARIO}>Secretario de célula</option>
                </NativeSelect>
              </div>
              <div className="space-y-2">
                <Label htmlFor="reg-member-id">{regMemberIdLabel}</Label>
                <Input
                  id="reg-member-id"
                  name="reg-member-id"
                  value={regMemberId}
                  onChange={(e) => setRegMemberId(e.target.value)}
                  placeholder="Lo proporciona pastoral"
                  required
                  autoComplete="off"
                />
                <p className="text-xs text-gray-500">{regMemberIdHelp}</p>
              </div>
              {error && (
                <div className="text-red-500 text-sm text-center bg-red-50 p-2 rounded">{error}</div>
              )}
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading}>
                {loading ? "Guardando..." : "Crear cuenta"}
              </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// ============ DASHBOARD COMPONENT ============
const Dashboard = ({ stats, members, cells }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [filteredCells, setFilteredCells] = useState([]);
  const [categoryTitle, setCategoryTitle] = useState("");
  const [showCells, setShowCells] = useState(false);

  const getCellName = useCallback((cellId) => {
    if (!cellId) return "Sin célula";
    const cell = cells.find(c => (c.id || c._id) === cellId);
    return cell ? cell.name : "Sin célula";
  }, [cells]);

  const getMemberName = (memberId) => {
    if (!memberId) return "Sin asignar";
    const member = members.find(m => (m.id || m._id) === memberId);
    return member ? member.name : "Sin asignar";
  };

  const getMemberTypeLabel = (type) => {
    const types = {
      "invitado_potencial": "Invitado Potencial",
      "invitado_primer_mes": "Invitado 1er Mes",
      "visita_regular": "Visita Regular",
      "visita_esporadica": "Visita Esporádica",
      "visita": "Visita",
      "miembro": "Miembro",
      "invitado": "Invitado",
      "lider": "Líder",
      "líder": "Líder",
      "supervisor": "Supervisor",
      "supervisora": "Supervisora"
    };
    return types[type] || type;
  };

  const handleCardClick = (category, title) => {
    let filtered = [];
    let isCellsView = false;
    
    switch(category) {
      case "cells":
        setFilteredCells(cells);
        isCellsView = true;
        break;
      case "total_members":
        filtered = members;
        break;
      case "miembros":
        filtered = members.filter(m => m.member_type === "miembro");
        break;
      case "inactivos":
        filtered = members.filter(m => m.inactive_2_months === true);
        break;
      case "cuidado_especial":
        filtered = members.filter(m => m.extra_care === true);
        break;
      case "activos_servicio":
        filtered = members.filter(m => m.active_in_service === true);
        break;
      case "total_visitas":
        filtered = members.filter(m => 
          m.member_type === "visita_regular" || 
          m.member_type === "visita_esporadica" || 
          m.member_type === "visita"
        );
        break;
      case "visita_regular":
        filtered = members.filter(m => m.member_type === "visita_regular");
        break;
      case "visita_esporadica":
        filtered = members.filter(m => m.member_type === "visita_esporadica");
        break;
      case "total_invitados":
        filtered = members.filter(m => 
          m.member_type === "invitado_potencial" || 
          m.member_type === "invitado_primer_mes" || 
          m.member_type === "invitado"
        );
        break;
      case "invitado_potencial":
        filtered = members.filter(m => m.member_type === "invitado_potencial");
        break;
      case "invitado_primer_mes":
        filtered = members.filter(m => m.member_type === "invitado_primer_mes");
        break;
      default:
        filtered = [];
    }
    
    setShowCells(isCellsView);
    if (!isCellsView) {
      setFilteredMembers(filtered);
      setFilteredCells([]);
    }
    setCategoryTitle(title);
    setSelectedCategory(category);
  };

  const closeModal = () => {
    setSelectedCategory(null);
    setFilteredMembers([]);
    setFilteredCells([]);
    setCategoryTitle("");
    setShowCells(false);
  };

  const birthdayData = useMemo(
    () => buildMonthBirthdays(members, getCellName),
    [members, getCellName]
  );

  const todayBirthdayRows = useMemo(
    () => birthdayData.sortedRows.filter((r) => r.isToday),
    [birthdayData.sortedRows]
  );
  const otherBirthdayRows = useMemo(() => {
    const rest = birthdayData.sortedRows.filter((r) => !r.isToday);
    return [...rest].sort((a, b) => {
      const ta = a.celebrationDate?.getTime?.() ?? 0;
      const tb = b.celebrationDate?.getTime?.() ?? 0;
      if (ta !== tb) return ta - tb;
      const an = String(a.member?.name || "").trim().toLocaleLowerCase("es");
      const bn = String(b.member?.name || "").trim().toLocaleLowerCase("es");
      return an.localeCompare(bn, "es");
    });
  }, [birthdayData.sortedRows]);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800">Dashboard</h2>
      
      {/* Modal de detalles */}
      <NativeDialog open={!!selectedCategory} onClose={closeModal} className={`w-full p-6 ${showCells ? "max-w-6xl" : "max-w-4xl"}`}>
        <h2 className="text-xl font-bold mb-4">
          {showCells
            ? `${categoryTitle} (${filteredCells.length})`
            : `${categoryTitle} (${filteredMembers.length})`}
        </h2>
        
        {/* Vista de Células — resumen completo agrupado por día */}
        {showCells && (
          <DashboardCellsOverview
            cells={filteredCells}
            members={members}
            getMemberName={getMemberName}
          />
        )}

        {/* Vista de Miembros */}
        {!showCells && (
          filteredMembers.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No hay personas en esta categoría</p>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Nombre</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Teléfono</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Célula</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Tipo</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Mentor</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMembers.map((member, index) => (
                    <tr key={member.id || member._id || index} className="border-b hover:bg-gray-50">
                      <td className="p-3">
                        <div className="font-medium text-gray-900">{member.name}</div>
                        {member.extra_care && (
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">Cuidado Especial</span>
                        )}
                        {member.inactive_2_months && (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full ml-1">Inactivo</span>
                        )}
                      </td>
                      <td className="p-3 text-gray-600">{member.phone || "Sin teléfono"}</td>
                      <td className="p-3 text-gray-600">{getCellName(member.cell_id)}</td>
                      <td className="p-3">
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                          {getMemberTypeLabel(member.member_type)}
                        </span>
                      </td>
                      <td className="p-3 text-gray-600">{formatMentor(member)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </NativeDialog>

      {/* Tarjetas principales - Fila 1 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div 
          className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg shadow cursor-pointer hover:shadow-lg transition-all hover:scale-105 p-4"
          onClick={() => handleCardClick("cells", "Total Células")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Total Células</p>
              <p className="text-3xl font-bold">{stats.total_cells || 0}</p>
            </div>
            <Building2 className="w-10 h-10 opacity-80" />
          </div>
        </div>

        <div 
          className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg shadow cursor-pointer hover:shadow-lg transition-all hover:scale-105 p-4"
          onClick={() => handleCardClick("total_members", "Personas totales")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Personas totales</p>
              <p className="text-3xl font-bold">{stats.total_members || 0}</p>
            </div>
            <Users className="w-10 h-10 opacity-80" />
          </div>
        </div>

        <div 
          className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg shadow cursor-pointer hover:shadow-lg transition-all hover:scale-105 p-4"
          onClick={() => handleCardClick("miembros", "Miembros")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Miembros</p>
              <p className="text-3xl font-bold">{stats.total_miembros || 0}</p>
            </div>
            <UserPlus className="w-10 h-10 opacity-80" />
          </div>
        </div>

        <div 
          className="bg-gradient-to-br from-red-500 to-red-600 text-white rounded-lg shadow cursor-pointer hover:shadow-lg transition-all hover:scale-105 p-4"
          onClick={() => handleCardClick("inactivos", "Inactivos (+2 meses)")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm">Inactivos (+2 meses)</p>
              <p className="text-3xl font-bold">{stats.total_inactivos || 0}</p>
            </div>
            <Users className="w-10 h-10 opacity-80" />
          </div>
        </div>
      </div>

      {/* Tarjetas principales - Fila 2 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div 
          className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-lg shadow cursor-pointer hover:shadow-lg transition-all hover:scale-105 p-4"
          onClick={() => handleCardClick("cuidado_especial", "Cuidado Especial")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Cuidado Especial</p>
              <p className="text-3xl font-bold">{stats.extra_care_count || 0}</p>
            </div>
            <Heart className="w-10 h-10 opacity-80" />
          </div>
        </div>

        <div 
          className="bg-gradient-to-br from-teal-500 to-teal-600 text-white rounded-lg shadow cursor-pointer hover:shadow-lg transition-all hover:scale-105 p-4"
          onClick={() => handleCardClick("activos_servicio", "Activos en Servicio")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-teal-100 text-sm">Activos en Servicio</p>
              <p className="text-3xl font-bold">{stats.active_in_service || 0}</p>
            </div>
            <Gift className="w-10 h-10 opacity-80" />
          </div>
        </div>

        <div
          className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white rounded-lg shadow cursor-pointer hover:shadow-lg transition-all hover:scale-105 p-4"
          onClick={() => handleCardClick("invitado_potencial", "Invitados Potenciales")}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleCardClick("invitado_potencial", "Invitados Potenciales");
            }
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-cyan-100 text-sm">Invitados Potenciales</p>
              <p className="text-3xl font-bold">{stats.invitado_potencial ?? 0}</p>
            </div>
            <UserPlus className="w-10 h-10 opacity-80" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-lime-500 to-lime-600 text-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lime-100 text-sm">Asistencia Semanal Promedio</p>
              <p className="text-3xl font-bold">{formatWeeklyAttendanceAvgDisplay(stats.weekly_attendance)}</p>
            </div>
            <Calendar className="w-10 h-10 opacity-80" />
          </div>
        </div>
      </div>

      {/* Cumpleañeros del mes */}
      <section
        className="rounded-2xl border border-indigo-100 bg-gradient-to-b from-white via-slate-50/40 to-indigo-50/30 p-5 shadow-md ring-1 ring-indigo-100/80"
        aria-labelledby="dashboard-birthdays-heading"
      >
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-md">
              <Cake className="h-5 w-5" aria-hidden />
            </div>
            <div>
              <h3 id="dashboard-birthdays-heading" className="text-lg font-semibold text-gray-900">
                Cumpleañeros del mes
              </h3>
              <p className="text-sm text-gray-500 capitalize">{birthdayData.monthTitle}</p>
            </div>
          </div>
        </div>

        {birthdayData.sortedRows.length === 0 ? (
          <p className="rounded-lg border border-dashed border-gray-200 bg-white/80 px-4 py-8 text-center text-gray-600">
            No hay cumpleañeros registrados este mes.
          </p>
        ) : (
          <div>
            {todayBirthdayRows.length > 0 ? (
              <div
                className="relative mb-6 overflow-hidden rounded-3xl border border-amber-200/70 bg-gradient-to-br from-amber-50/95 via-white to-rose-50/90 p-6 shadow-[0_24px_60px_-16px_rgba(180,83,9,0.22)] ring-1 ring-amber-300/25 sm:p-8"
                role="region"
                aria-label={todayBirthdayRows.length === 1 ? "Cumpleañero del día" : "Cumpleañeros del día"}
              >
                <div
                  className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-gradient-to-br from-amber-300/35 to-rose-300/25 blur-3xl"
                  aria-hidden
                />
                <div
                  className="pointer-events-none absolute -bottom-20 -left-20 h-56 w-56 rounded-full bg-gradient-to-tr from-violet-300/25 to-fuchsia-200/20 blur-3xl"
                  aria-hidden
                />
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.65),transparent_55%)]" aria-hidden />
                <div className="relative">
                  <div className="mb-5 flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-white shadow-md shadow-amber-900/20">
                      <Gift className="h-4 w-4 shrink-0" aria-hidden />
                      {todayBirthdayRows.length === 1 ? "Cumpleañero del día" : "Cumpleañeros del día"}
                    </span>
                    <span className="text-sm font-medium text-amber-950/70">¡Felicidades!</span>
                  </div>
                  <div className="space-y-6">
                    {todayBirthdayRows.map((row, idx) => {
                      const showAge = row.ageTurning != null && row.ageTurning >= 1;
                      return (
                        <div
                          key={row.member.id || row.member._id}
                          className={idx > 0 ? "border-t border-amber-200/60 pt-6 sm:pt-6" : ""}
                        >
                          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                            <div className="min-w-0 flex-1">
                              <p className="text-2xl font-bold leading-tight tracking-tight text-gray-900 sm:text-3xl lg:text-4xl">
                                {row.member.name || "—"}
                              </p>
                              <p className="mt-2 text-sm font-medium capitalize text-amber-900/85 sm:text-base">
                                {row.displayDate}
                              </p>
                              <p className="mt-1 text-sm text-gray-600">
                                <span className="text-gray-500">Célula:</span>{" "}
                                <span className="font-medium text-gray-800">{row.cellName}</span>
                              </p>
                            </div>
                            <div className="flex shrink-0 flex-wrap items-center gap-3">
                              {showAge ? (
                                <div className="flex items-center gap-2 rounded-2xl border border-amber-300/70 bg-white/95 px-5 py-3 shadow-inner shadow-amber-100/80 ring-1 ring-white/80">
                                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-md">
                                    <Cake className="h-6 w-6" aria-hidden />
                                  </div>
                                  <div>
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-800/80">
                                      Cumple
                                    </p>
                                    <p className="text-xl font-bold tabular-nums text-gray-900 sm:text-2xl">
                                      {row.ageTurning}{" "}
                                      <span className="text-base font-semibold text-gray-600 sm:text-lg">años</span>
                                    </p>
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : null}

            {otherBirthdayRows.length > 0 ? (
              <>
                <p className="mb-2 flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-emerald-800">
                  <Calendar className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span>
                    {todayBirthdayRows.length > 0
                      ? "Otros cumpleañeros del mes, ordenados por fecha del calendario (día menor a día mayor)."
                      : "Orden: por fecha del mes, del día menor al mayor (por ejemplo 4, luego 10, luego 27 de mayo)."}
                  </span>
                </p>
                <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                  <table className="w-full min-w-[640px] border-collapse text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                        <th className="px-3 py-3 sm:px-4">Nombre</th>
                        <th className="px-3 py-3 sm:px-4">Célula</th>
                        <th className="px-3 py-3 sm:px-4">Fecha</th>
                        <th className="px-3 py-3 sm:px-4">Días faltantes</th>
                        <th className="px-3 py-3 text-center sm:px-4">Edad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {otherBirthdayRows.map((row) => {
                        const showAge = row.ageTurning != null && row.ageTurning >= 1;
                        const rowTint = row.isUpcomingWithin3Days
                          ? "bg-teal-50/90 border-l-4 border-l-teal-500"
                          : "border-l-4 border-l-transparent hover:bg-slate-50/70";
                        return (
                          <tr
                            key={row.member.id || row.member._id}
                            className={`border-b border-gray-100 transition-colors last:border-0 ${rowTint}`}
                          >
                            <td className="px-3 py-3 font-medium text-gray-900 sm:px-4">
                              <span className="inline-flex items-center gap-2">
                                <span>{row.member.name || "—"}</span>
                              </span>
                            </td>
                            <td className="px-3 py-3 text-gray-700 sm:px-4">{row.cellName}</td>
                            <td className="px-3 py-3 capitalize text-gray-800 sm:px-4">
                              <span className="sm:hidden">{row.displayDateShort}</span>
                              <span className="hidden sm:inline">{row.displayDate}</span>
                            </td>
                            <td className="px-3 py-3 sm:px-4">
                              <span
                                className={`inline-block font-medium tabular-nums ${
                                  row.isUpcomingWithin3Days ? "text-teal-800" : "text-gray-700"
                                }`}
                              >
                                {row.daysLabel}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-center tabular-nums text-gray-800 sm:px-4">
                              {showAge ? (
                                <span className="inline-block rounded-full bg-white/80 px-2 py-0.5 text-xs font-semibold text-emerald-900 ring-1 ring-emerald-200/90">
                                  {row.ageTurning} años
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            ) : todayBirthdayRows.length > 0 ? (
              <p className="rounded-xl border border-dashed border-amber-200/80 bg-amber-50/40 px-4 py-3 text-center text-sm text-amber-900/80">
                No hay más cumpleañeros registrados este mes aparte de{" "}
                {todayBirthdayRows.length === 1 ? "quien cumple hoy" : "quienes cumplen hoy"}.
              </p>
            ) : null}
          </div>
        )}
      </section>

      {/* Sección de Visitas */}
      <div>
        <h3 className="text-lg font-semibold text-gray-700 mb-3">Visitas</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div 
            className="bg-white border-l-4 border-l-blue-500 rounded-lg shadow p-4 cursor-pointer hover:shadow-lg transition-all"
            onClick={() => handleCardClick("total_visitas", "Total Visitas")}
          >
            <p className="text-gray-500 text-sm">Total Visitas</p>
            <p className="text-2xl font-bold text-blue-600">{stats.total_visitas || 0}</p>
          </div>
          <div 
            className="bg-white border-l-4 border-l-purple-500 rounded-lg shadow p-4 cursor-pointer hover:shadow-lg transition-all"
            onClick={() => handleCardClick("visita_regular", "Visita Regular")}
          >
            <p className="text-gray-500 text-sm">Visita Regular</p>
            <p className="text-2xl font-bold text-purple-600">{stats.visita_regular || 0}</p>
          </div>
          <div 
            className="bg-white border-l-4 border-l-yellow-500 rounded-lg shadow p-4 cursor-pointer hover:shadow-lg transition-all"
            onClick={() => handleCardClick("visita_esporadica", "Visita Esporádica")}
          >
            <p className="text-gray-500 text-sm">Visita Esporádica</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.visita_esporadica || 0}</p>
          </div>
        </div>
      </div>

      {/* Sección de Invitados */}
      <div>
        <h3 className="text-lg font-semibold text-gray-700 mb-3">Invitados</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div 
            className="bg-white border-l-4 border-l-green-500 rounded-lg shadow p-4 cursor-pointer hover:shadow-lg transition-all"
            onClick={() => handleCardClick("total_invitados", "Total Invitados")}
          >
            <p className="text-gray-500 text-sm">Total Invitados</p>
            <p className="text-2xl font-bold text-green-600">{stats.total_invitados || 0}</p>
          </div>
          <div 
            className="bg-white border-l-4 border-l-pink-500 rounded-lg shadow p-4 cursor-pointer hover:shadow-lg transition-all"
            onClick={() => handleCardClick("invitado_potencial", "Invitados Potenciales")}
          >
            <p className="text-gray-500 text-sm">Invitado Potencial</p>
            <p className="text-2xl font-bold text-pink-600">{stats.invitado_potencial || 0}</p>
          </div>
          <div 
            className="bg-white border-l-4 border-l-indigo-500 rounded-lg shadow p-4 cursor-pointer hover:shadow-lg transition-all"
            onClick={() => handleCardClick("invitado_primer_mes", "Invitado 1er Mes")}
          >
            <p className="text-gray-500 text-sm">Invitado 1er Mes</p>
            <p className="text-2xl font-bold text-indigo-600">{stats.invitado_primer_mes || 0}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

/** parent_cell_id unificado (API puede venir de parent_cell_id o celula_referencia_id legacy). */
function resolveParentCellId(cell) {
  return String(cell?.parent_cell_id || cell?.celula_referencia_id || "").trim();
}

const CELL_ROLE_FIELDS = ["leader_id", "co_leader_id", "secretary_id", "treasurer_id", "host_id"];

const CELL_NETWORK_LABELS = {
  discipulado_pastores: "Discipulado Pastores",
  adultos: "Adultos",
  jovenes: "Jóvenes",
  ninos: "Niños",
};

function formatCellNetworkLabel(networkType) {
  const key = String(networkType || "").trim();
  return CELL_NETWORK_LABELS[key] || key || "Sin clasificar";
}

function formatTrashDeletedAt(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" });
}

const CELL_PERMANENT_DELETE_CONFIRM =
  "Esta acción eliminará permanentemente la célula y no podrá restaurarse. ¿Desea continuar?";

function TrashPermanentPasswordDialog({ open, title, description, onClose, onConfirm, submitting }) {
  const [password, setPassword] = useState("");
  useEffect(() => {
    if (open) setPassword("");
  }, [open]);
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!String(password).trim()) {
      alert("La contraseña es obligatoria");
      return;
    }
    onConfirm(String(password));
  };
  return (
    <NativeDialog open={open} onClose={onClose} className="w-full max-w-md p-6">
      <h2 className="text-xl font-bold mb-2 text-red-800">{title}</h2>
      <p className="text-sm text-gray-600 mb-4">{description}</p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label>Contraseña de confirmación *</Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Ingrese la contraseña"
            required
            autoComplete="off"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" variant="destructive" disabled={submitting}>
            {submitting ? "Eliminando…" : "Eliminar definitivamente"}
          </Button>
        </div>
      </form>
    </NativeDialog>
  );
}

/** Papelera de células (módulo Células): listar eliminadas, restaurar y eliminar definitivamente. */
const CellsTrashDialog = ({ open, onClose, canRestore, canPermanentDelete, members, onRestored }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [permanentTarget, setPermanentTarget] = useState(null);

  const loadTrash = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/cells/trash");
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setItems([]);
      alert(formatApiError(err) || "No se pudo cargar la papelera de células");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadTrash();
  }, [open, loadTrash]);

  const getMemberName = (memberId) => {
    if (!memberId) return "Sin asignar";
    const member = members.find((m) => String(m.id || m._id) === String(memberId));
    return member ? member.name : "Sin asignar";
  };

  const handleRestore = async (cellId) => {
    const id = getCellMongoId(cellId);
    if (!id) {
      alert("ID de célula inválido");
      return;
    }
    if (!window.confirm("¿Restaurar esta célula? Volverá a aparecer en la lista de células activas.")) return;
    setRestoringId(id);
    try {
      const res = await api.patch(`/cells/${id}/restore`);
      alert(res.data?.message || "Célula restaurada correctamente");
      await loadTrash();
      onRestored?.();
    } catch (err) {
      alert(formatApiError(err) || "Error al restaurar la célula");
    } finally {
      setRestoringId("");
    }
  };

  const requestPermanentDelete = (cell) => {
    const id = getCellMongoId(cell);
    if (!id) {
      alert("ID de célula inválido");
      return;
    }
    if (!window.confirm(CELL_PERMANENT_DELETE_CONFIRM)) return;
    setPermanentTarget(cell);
  };

  const handlePermanentDeleteConfirm = async (password) => {
    const id = getCellMongoId(permanentTarget);
    if (!id) return;
    setDeletingId(id);
    try {
      const res = await api.delete(`/cells/${id}/permanent`, { data: { password } });
      alert(res.data?.message || "Célula eliminada definitivamente");
      setPermanentTarget(null);
      await loadTrash();
      onRestored?.();
    } catch (err) {
      alert(formatApiError(err) || "Error al eliminar definitivamente la célula");
    } finally {
      setDeletingId("");
    }
  };

  const showActions = canRestore || canPermanentDelete;

  return (
    <>
    <NativeDialog open={open} onClose={onClose} className="w-full max-w-4xl p-6 max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-start gap-4 mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Papelera de células</h2>
          <p className="text-sm text-gray-600 mt-1">
            Células eliminadas (borrado lógico).
            {canRestore ? " Un pastor puede restaurarlas." : ""}
            {canPermanentDelete ? " Puede eliminarlas definitivamente si no tienen datos relacionados." : ""}
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Cerrar
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-10 h-10 border-4 border-amber-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No hay células en la papelera.</p>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-3 font-semibold text-gray-700">Célula</th>
                <th className="text-left p-3 font-semibold text-gray-700">Red</th>
                <th className="text-left p-3 font-semibold text-gray-700">Líder</th>
                <th className="text-left p-3 font-semibold text-gray-700">Eliminada</th>
                <th className="text-left p-3 font-semibold text-gray-700">Por</th>
                {showActions ? (
                  <th className="text-right p-3 font-semibold text-gray-700">Acciones</th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                const rowId = getCellMongoId(row);
                const leaderLabel = row.leader_name || getMemberName(row.leader_id);
                return (
                  <tr key={rowId || row.name} className="border-b hover:bg-gray-50">
                    <td className="p-3 font-medium text-gray-900">{row.name}</td>
                    <td className="p-3 text-gray-600">{formatCellNetworkLabel(row.network_type)}</td>
                    <td className="p-3 text-gray-600">{leaderLabel}</td>
                    <td className="p-3 text-gray-600 whitespace-nowrap">{formatTrashDeletedAt(row.deleted_at)}</td>
                    <td className="p-3 text-gray-600">{row.deleted_by_username || "—"}</td>
                    {showActions ? (
                      <td className="p-3 text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          {canRestore ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={restoringId === rowId || deletingId === rowId}
                              onClick={() => handleRestore(rowId)}
                            >
                              <ArchiveRestore className="w-4 h-4 mr-1" />
                              {restoringId === rowId ? "Restaurando…" : "Restaurar"}
                            </Button>
                          ) : null}
                          {canPermanentDelete ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="destructive"
                              disabled={restoringId === rowId || deletingId === rowId}
                              onClick={() => requestPermanentDelete(row)}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              {deletingId === rowId ? "Eliminando…" : "Eliminar definitivamente"}
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </NativeDialog>
    <TrashPermanentPasswordDialog
      open={Boolean(permanentTarget)}
      title="Eliminar célula definitivamente"
      description={
        permanentTarget
          ? `Confirme con contraseña la eliminación permanente de «${permanentTarget.name}».`
          : ""
      }
      onClose={() => !deletingId && setPermanentTarget(null)}
      onConfirm={handlePermanentDeleteConfirm}
      submitting={Boolean(deletingId)}
    />
    </>
  );
};

// ============ CELLS COMPONENT ============
const Cells = ({
  cells,
  members,
  onRefresh,
  canCreateCell,
  canDeleteCell,
  canViewCellsTrash,
  canRestoreCellTrash,
  canPermanentDeleteCellTrash,
  cellEditableIds,
}) => {
  const [showTrash, setShowTrash] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [roleConflict, setRoleConflict] = useState("");
  const [editingParentLabel, setEditingParentLabel] = useState("");
  const [selectedCell, setSelectedCell] = useState(null);
  const [cellStats, setCellStats] = useState(null);
  const [formData, setFormData] = useState({
    address: "",
    location: "",
    parent_cell_id: "",
    leader_id: "",
    co_leader_id: "",
    secretary_id: "",
    treasurer_id: "",
    host_id: "",
    network_type: "adultos",
    meeting_modality: "",
    meeting_day: "",
    meeting_time: "",
  });

  const isCellEditable = (cell) => {
    const id = getCellMongoId(cell);
    if (!id) return false;
    if (cellEditableIds === null) return true;
    return cellEditableIds.has(id);
  };

  const resetForm = () => {
    setFormData({
      address: "",
      location: "",
      parent_cell_id: "",
      leader_id: "",
      co_leader_id: "",
      secretary_id: "",
      treasurer_id: "",
      host_id: "",
      network_type: "adultos",
      meeting_modality: "",
      meeting_day: "",
      meeting_time: "",
    });
    setEditingCell(null);
    setRoleConflict("");
    setEditingParentLabel("");
    setShowForm(false);
  };

  const roleIdsInOtherCells = useMemo(() => {
    const used = new Set();
    for (const c of cells || []) {
      const cid = getCellMongoId(c);
      if (!cid || (editingCell && cid === String(editingCell))) continue;
      for (const field of CELL_ROLE_FIELDS) {
        const roleId = String(c[field] || "").trim();
        if (roleId) used.add(roleId);
      }
    }
    return used;
  }, [cells, editingCell]);

  /** cell_id donde el miembro está registrado como miembro (no es el cargo). */
  const memberAssignedHomeCellId = useCallback((memberId) => {
    const sid = String(memberId || "").trim();
    if (!sid) return "";
    const m = members.find((mem) => String(mem.id || mem._id) === sid);
    return String(m?.cell_id || "").trim();
  }, [members]);

  const hasWrongMemberHomeCellForForm = useCallback(
    (_fieldName, memberId) => {
      const sid = String(memberId || "").trim();
      if (!sid) return false;
      const home = memberAssignedHomeCellId(sid);
      if (!home) return false;
      if (!editingCell) return true;
      return home !== String(editingCell);
    },
    [editingCell, memberAssignedHomeCellId],
  );

  const hasRoleConflictInForm = useCallback(
    (fieldName, memberId) => {
      const sid = String(memberId || "").trim();
      if (!sid) return false;
      return CELL_ROLE_FIELDS.some((field) => field !== fieldName && String(formData[field] || "").trim() === sid);
    },
    [formData],
  );

  const canAssignRoleMember = useCallback(
    (fieldName, memberId) => {
      const sid = String(memberId || "").trim();
      if (!sid) return true;
      if (roleIdsInOtherCells.has(sid)) return false;
      if (hasWrongMemberHomeCellForForm(fieldName, sid)) return false;
      if (hasRoleConflictInForm(fieldName, sid)) return false;
      return true;
    },
    [hasRoleConflictInForm, hasWrongMemberHomeCellForForm, roleIdsInOtherCells],
  );

  const WRONG_MEMBER_HOME_DETAIL =
    "Esta persona pertenece a otra célula. Para asignarle un cargo aquí, primero debe cambiarla de célula.";

  const setRoleField = useCallback(
    (fieldName, memberId) => {
      const sid = String(memberId || "").trim();
      setRoleConflict("");
      if (!sid) {
        setFormData((prev) => ({ ...prev, [fieldName]: "" }));
        return;
      }
      if (roleIdsInOtherCells.has(sid)) {
        setRoleConflict("Esta persona ya tiene un cargo asignado en otra célula.");
        return;
      }
      if (hasWrongMemberHomeCellForForm(fieldName, sid)) {
        setRoleConflict(WRONG_MEMBER_HOME_DETAIL);
        return;
      }
      if (hasRoleConflictInForm(fieldName, sid)) {
        setRoleConflict("Una misma persona no puede ocupar dos cargos distintos en la misma célula.");
        return;
      }
      setFormData((prev) => ({ ...prev, [fieldName]: sid }));
    },
    [hasRoleConflictInForm, hasWrongMemberHomeCellForForm, roleIdsInOtherCells],
  );

  const parentLinkSelectOptions = useMemo(() => {
    const opts = [];
    const seen = new Set();
    for (const c of cells) {
      const id = getCellMongoId(c);
      if (!id || id === String(editingCell || "")) continue;
      if (seen.has(id)) continue;
      seen.add(id);
      opts.push({ id, name: c.name || id });
    }
    const pid = String(formData.parent_cell_id || "").trim();
    if (pid && !seen.has(pid)) {
      opts.push({
        id: pid,
        name: editingParentLabel || cells.find((c) => getCellMongoId(c) === pid)?.name || pid,
      });
    }
    return opts;
  }, [cells, editingCell, formData.parent_cell_id, editingParentLabel]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingCell && !canCreateCell) {
      alert("No tiene permiso para crear células.");
      return;
    }
    if (!formData.network_type) {
      alert("Seleccione la red de la célula.");
      return;
    }
    const normalizedRoleIds = CELL_ROLE_FIELDS.map((field) => String(formData[field] || "").trim()).filter(Boolean);
    if (new Set(normalizedRoleIds).size !== normalizedRoleIds.length) {
      setRoleConflict("Una misma persona no puede ocupar dos cargos distintos en la misma célula.");
      return;
    }
    if (normalizedRoleIds.some((memberId) => roleIdsInOtherCells.has(memberId))) {
      setRoleConflict("Esta persona ya tiene un cargo asignado en otra célula.");
      return;
    }
    if (normalizedRoleIds.some((mid) => hasWrongMemberHomeCellForForm("", mid))) {
      setRoleConflict(WRONG_MEMBER_HOME_DETAIL);
      return;
    }
    try {
      if (editingCell) {
        const cellId = getCellMongoId(editingCell);
        if (!cellId) {
          alert("No se pudo obtener el ID de la célula para actualizar.");
          logCellApiDebug("PUT blocked", { editingCell, reason: "invalid cell id" });
          return;
        }
        const endpoint = `/cells/${cellId}`;
        logCellApiDebug("PUT request", { cellId, endpoint, formData });
        const res = await api.put(endpoint, formData);
        logCellApiDebug("PUT response", { cellId, endpoint, status: res.status, data: res.data });
      } else {
        logCellApiDebug("POST request", { endpoint: "/cells", formData });
        const res = await api.post("/cells", formData);
        logCellApiDebug("POST response", {
          endpoint: "/cells",
          status: res.status,
          cellId: getCellMongoId(res.data),
          data: res.data,
        });
      }
      resetForm();
      onRefresh();
    } catch (err) {
      logCellApiDebug("save error", {
        editingCell,
        cellId: getCellMongoId(editingCell),
        status: err.response?.status,
        detail: err.response?.data?.detail,
      });
      alert(formatApiError(err) || "Error al guardar célula");
    }
  };

  const applyCellToForm = (cell) => {
    const cellId = getCellMongoId(cell);
    setFormData({
      address: cell.address || "",
      location: cell.location || "",
      parent_cell_id: resolveParentCellId(cell),
      leader_id: cell.leader_id || "",
      co_leader_id: cell.co_leader_id || "",
      secretary_id: cell.secretary_id || "",
      treasurer_id: cell.treasurer_id || "",
      host_id: cell.host_id || "",
      network_type: cell.network_type || "",
      meeting_modality: cell.meeting_modality || "",
      meeting_day: cell.meeting_day || "",
      meeting_time: cell.meeting_time || "",
    });
    setRoleConflict("");
    setEditingParentLabel(cell.parent_cell_name || "");
    setEditingCell(cellId || null);
    setShowForm(true);
  };

  const handleEdit = async (cell) => {
    if (!isCellEditable(cell)) return;
    const id = getCellMongoId(cell);
    if (!id) {
      alert("No se pudo obtener el ID de la célula para editar.");
      logCellApiDebug("GET blocked", { cell, reason: "invalid cell id" });
      return;
    }
    const endpoint = `/cells/${id}`;
    try {
      logCellApiDebug("GET request", { cellId: id, endpoint });
      const res = await api.get(endpoint);
      logCellApiDebug("GET response", { cellId: id, endpoint, status: res.status, data: res.data });
      applyCellToForm(normalizeCellFromApi(res.data));
    } catch (err) {
      logCellApiDebug("GET error", { cellId: id, endpoint, status: err.response?.status, detail: err.response?.data?.detail });
      if (err.response?.status === 404) {
        alert("La célula ya no está en su alcance o fue eliminada. Actualizando lista…");
        onRefresh();
        return;
      }
      applyCellToForm(cell);
    }
  };

  const handleDelete = async (cell) => {
    const id = getCellMongoId(cell);
    if (!id) {
      alert("No se pudo obtener el ID de la célula para eliminar.");
      logCellApiDebug("DELETE blocked", { cell, reason: "invalid cell id" });
      return;
    }
    if (window.confirm("¿Mover esta célula a la papelera? Podrá restaurarse con un administrador autorizado.")) {
      const endpoint = `/cells/${id}`;
      try {
        logCellApiDebug("DELETE request", { cellId: id, endpoint, cell });
        const res = await api.delete(endpoint);
        logCellApiDebug("DELETE response", { cellId: id, endpoint, status: res.status, data: res.data });
        onRefresh();
      } catch (err) {
        logCellApiDebug("DELETE error", { cellId: id, endpoint, status: err.response?.status, detail: err.response?.data?.detail });
        alert(formatApiError(err) || "Error al eliminar");
      }
    }
  };

  const handleViewStats = async (cell) => {
    const cellId = getCellMongoId(cell);
    if (!cellId) {
      alert("No se pudo obtener el ID de la célula para estadísticas.");
      return;
    }
    setSelectedCell(cell);
    const endpoint = `/stats/cell/${cellId}`;
    try {
      logCellApiDebug("stats GET request", { cellId, endpoint });
      const response = await api.get(endpoint);
      logCellApiDebug("stats GET response", { cellId, endpoint, status: response.status });
      setCellStats(response.data);
    } catch (err) {
      logCellApiDebug("stats GET error", { cellId, endpoint, status: err.response?.status, detail: err.response?.data?.detail });
      console.error(err);
    }
  };

  const getMemberName = (memberId) => {
    if (!memberId) return "Sin asignar";
    const member = members.find(m => (m.id || m._id) === memberId);
    return member ? member.name : "Sin asignar";
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <h2 className="text-2xl font-bold text-gray-800">Células</h2>
        <div className="flex flex-wrap gap-2">
          {canViewCellsTrash && (
            <Button
              type="button"
              variant="outline"
              className="border-amber-300 text-amber-900 hover:bg-amber-50"
              onClick={() => setShowTrash(true)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Papelera
            </Button>
          )}
          {canCreateCell && (
            <Button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" /> Nueva Célula
            </Button>
          )}
        </div>
      </div>

      <CellsTrashDialog
        open={showTrash}
        onClose={() => setShowTrash(false)}
        canRestore={canRestoreCellTrash}
        canPermanentDelete={canPermanentDeleteCellTrash}
        members={members}
        onRestored={onRefresh}
      />

      {/* Form Dialog */}
      <NativeDialog open={showForm} onClose={resetForm} className="w-full max-w-2xl p-6">
        <h2 className="text-xl font-bold mb-4">{editingCell ? "Editar Célula" : "Nueva Célula"}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Red de la célula *</Label>
              <NativeSelect
                value={formData.network_type}
                onChange={(v) => setFormData({ ...formData, network_type: v })}
                placeholder="Seleccione la red *"
              >
                <option value="discipulado_pastores">Discipulado Pastores</option>
                <option value="adultos">Adultos</option>
                <option value="jovenes">Jóvenes</option>
                <option value="ninos">Niños</option>
              </NativeSelect>
              <p className="text-xs text-gray-500">
                «Discipulado Pastores» es la red de la célula raíz de pastores; las demás suelen ser Adultos, Jóvenes o
                Niños.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cell-parent-link-select" className="text-gray-600">
                {CELL_OPTIONAL_PARENT_LINK_LABEL}
              </Label>
              <NativeSelect
                id="cell-parent-link-select"
                value={formData.parent_cell_id}
                onChange={(v) => setFormData({ ...formData, parent_cell_id: v })}
                placeholder="Sin enlace"
              >
                {parentLinkSelectOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </NativeSelect>
              {formData.parent_cell_id && editingParentLabel ? (
                <p className="text-xs text-gray-600">
                  Enlace actual: <span className="font-medium text-gray-800">{editingParentLabel}</span>
                </p>
              ) : null}
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Dirección *</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                required
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Ubicación/Zona</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Modalidad de reunión</Label>
              <NativeSelect
                value={formData.meeting_modality}
                onChange={(v) => setFormData({ ...formData, meeting_modality: v })}
                placeholder="Seleccionar modalidad"
              >
                {CELL_MEETING_MODALITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="space-y-2">
              <Label>Día de reunión</Label>
              <NativeSelect
                value={formData.meeting_day}
                onChange={(v) => setFormData({ ...formData, meeting_day: v })}
                placeholder="Seleccionar día"
              >
                {CELL_MEETING_DAY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cell-meeting-time">Hora de reunión</Label>
              <Input
                id="cell-meeting-time"
                type="time"
                value={formData.meeting_time || ""}
                onChange={(e) => setFormData({ ...formData, meeting_time: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Líder</Label>
              <NativeSelect
                value={formData.leader_id}
                onChange={(v) => setRoleField("leader_id", v)}
                placeholder="Sin asignar"
              >
                {members.map(member => (
                  <option
                    key={member.id || member._id}
                    value={member.id || member._id}
                    disabled={!canAssignRoleMember("leader_id", member.id || member._id)}
                  >
                    {member.name}
                  </option>
                ))}
              </NativeSelect>
              {roleConflict ? <p className="text-sm text-red-600">{roleConflict}</p> : null}
            </div>
            <div className="space-y-2">
              <Label>Co-Líder</Label>
              <NativeSelect
                value={formData.co_leader_id}
                onChange={(v) => setRoleField("co_leader_id", v)}
                placeholder="Sin asignar"
              >
                {members.map(member => (
                  <option
                    key={member.id || member._id}
                    value={member.id || member._id}
                    disabled={!canAssignRoleMember("co_leader_id", member.id || member._id)}
                  >
                    {member.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="space-y-2">
              <Label>Secretario(a)</Label>
              <NativeSelect
                value={formData.secretary_id}
                onChange={(v) => setRoleField("secretary_id", v)}
                placeholder="Sin asignar"
              >
                {members.map(member => (
                  <option
                    key={member.id || member._id}
                    value={member.id || member._id}
                    disabled={!canAssignRoleMember("secretary_id", member.id || member._id)}
                  >
                    {member.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="space-y-2">
              <Label>Tesorero(a)</Label>
              <NativeSelect
                value={formData.treasurer_id}
                onChange={(v) => setRoleField("treasurer_id", v)}
                placeholder="Sin asignar"
              >
                {members.map(member => (
                  <option
                    key={member.id || member._id}
                    value={member.id || member._id}
                    disabled={!canAssignRoleMember("treasurer_id", member.id || member._id)}
                  >
                    {member.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="space-y-2">
              <Label>Anfitrión</Label>
              <NativeSelect
                value={formData.host_id}
                onChange={(v) => setRoleField("host_id", v)}
                placeholder="Sin asignar"
              >
                {members.map(member => (
                  <option
                    key={member.id || member._id}
                    value={member.id || member._id}
                    disabled={!canAssignRoleMember("host_id", member.id || member._id)}
                  >
                    {member.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={resetForm}>Cancelar</Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
              {editingCell ? "Actualizar" : "Crear"}
            </Button>
          </div>
        </form>
      </NativeDialog>

      {/* Cell Stats Dialog */}
      <NativeDialog 
        open={!!selectedCell} 
        onClose={() => { setSelectedCell(null); setCellStats(null); }}
        className="w-full max-w-md p-6"
      >
        <h2 className="text-xl font-bold mb-4">Estadísticas: {selectedCell?.name}</h2>
        {cellStats && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-blue-50 p-3 rounded-lg text-center">
                <p className="text-sm text-gray-600">Total Miembros</p>
                <p className="text-2xl font-bold text-blue-600">{cellStats.total_members}</p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg text-center">
                <p className="text-sm text-gray-600">Miembros</p>
                <p className="text-2xl font-bold text-green-600">{cellStats.miembros}</p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg text-center">
                <p className="text-sm text-gray-600">Visita Regular</p>
                <p className="text-2xl font-bold text-purple-600">{cellStats.visita_regular}</p>
              </div>
              <div className="bg-yellow-50 p-3 rounded-lg text-center">
                <p className="text-sm text-gray-600">Visita Esporádica</p>
                <p className="text-2xl font-bold text-yellow-600">{cellStats.visita_esporadica}</p>
              </div>
              <div className="bg-indigo-50 p-3 rounded-lg text-center">
                <p className="text-sm text-gray-600">Invitado 1er Mes</p>
                <p className="text-2xl font-bold text-indigo-600">{cellStats.invitado_primer_mes}</p>
              </div>
              <div className="bg-pink-50 p-3 rounded-lg text-center">
                <p className="text-sm text-gray-600">Invitado Potencial</p>
                <p className="text-2xl font-bold text-pink-600">{cellStats.invitado_potencial}</p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg text-center">
                <p className="text-sm text-gray-600">Inactivos</p>
                <p className="text-2xl font-bold text-red-600">{cellStats.inactive_members}</p>
              </div>
              <div className="bg-orange-50 p-3 rounded-lg text-center">
                <p className="text-sm text-gray-600">Cuidado Especial</p>
                <p className="text-2xl font-bold text-orange-600">{cellStats.extra_care}</p>
              </div>
            </div>
            <div className="bg-teal-50 p-3 rounded-lg text-center">
              <p className="text-sm text-gray-600">Activos en Servicio</p>
              <p className="text-2xl font-bold text-teal-600">{cellStats.active_in_service}</p>
            </div>
          </div>
        )}
      </NativeDialog>

      {/* Cells List */}
      <div className="grid gap-4">
        {cells.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              {canCreateCell
                ? "No hay células registradas. Crea la primera célula."
                : "No hay células en su alcance por ahora."}
            </CardContent>
          </Card>
        ) : (
          cells.map(cell => (
            <Card key={getCellMongoId(cell) || cell.name} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-800">{cell.name}</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                      <p><MapPin className="w-4 h-4 inline mr-1" />{cell.address}</p>
                      <p><Building2 className="w-4 h-4 inline mr-1" />{cell.location || "Sin zona"}</p>
                      <p><Users className="w-4 h-4 inline mr-1" />Co-Líder: {getMemberName(cell.co_leader_id)}</p>
                      {resolveParentCellId(cell) ? (
                        <p className="md:col-span-2 text-gray-700">
                          Enlace:{" "}
                          <span className="font-medium">{cell.parent_cell_name || "Célula de referencia"}</span>
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleViewStats(cell)} title="Estadísticas">
                      <BarChart3 className="w-4 h-4" />
                    </Button>
                    {isCellEditable(cell) && (
                      <Button size="sm" variant="outline" onClick={() => handleEdit(cell)} title="Editar">
                        <Edit className="w-4 h-4" />
                      </Button>
                    )}
                    {canDeleteCell && (
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(cell)} title="Eliminar">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};


/** Pastor/admin: todas; supervisor: subárbol; líder/secretario: células propias (sin hijas por parent). */
function cellEditableIdSetForUser(user, cells, members) {
  if (!user) return new Set();
  if (isFullAccess(user)) return null;
  if (isSupervisor(user)) return new Set(cells.map((c) => getCellMongoId(c)).filter(Boolean));
  if (isCellStaff(user)) {
    const mid = String(user.member_id || "");
    if (!mid) return new Set();
    const s = new Set();
    for (const c of cells) {
      const cid = getCellMongoId(c);
      if (!cid) continue;
      if (String(c.leader_id || "") === mid || String(c.co_leader_id || "") === mid) s.add(cid);
      if (String(c.secretary_id || "") === mid) s.add(cid);
    }
    const mem = members.find((m) => String(m.id || m._id) === mid);
    if (mem?.cell_id) s.add(String(mem.cell_id));
    return s;
  }
  return new Set();
}

// ============ INFORMES (Pastor / Supervisor) ============
const ReportsPastoral = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const res = await api.get("/reports/pastoral-summary");
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (e.response?.status === 403) {
          if (!cancelled) setErr("No tiene permiso para ver informes consolidados.");
        } else {
          if (!cancelled) setErr("No se pudo cargar el informe.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (err) {
    return <p className="text-center text-red-600 py-8">{err}</p>;
  }
  if (!data) return null;

  const d = data.dashboard || {};
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <FileText className="w-7 h-7 text-blue-600" />
          Informes pastorales
        </h2>
        <p className="text-gray-600 mt-1 text-sm">
          Vista consolidada del ecosistema general. Generado: {data.generated_at ? new Date(data.generated_at).toLocaleString("es") : "—"}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          ["Células", d.total_cells],
          ["Personas", d.total_members],
          ["Miembros", d.total_miembros],
          ["Visitas", d.total_visitas],
          ["Invitados (tipos)", d.total_invitados],
          ["Inactivos +2 meses", d.total_inactivos],
          ["Cuidado especial", d.extra_care_count],
          ["En servicio", d.active_in_service],
          ["Invitados potenciales", d.invitado_potencial],
          ["Asistencia semanal promedio (reporte rápido)", formatWeeklyAttendanceAvgDisplay(d.weekly_attendance)],
          ["Registros asistencia (total)", data.attendance_records_total],
        ].map(([label, val]) => (
          <Card key={label}>
            <CardContent className="p-4">
              <p className="text-gray-500 text-sm">{label}</p>
              <p className="text-2xl font-bold text-blue-900">{val ?? 0}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <Card><CardContent className="p-4"><p className="text-gray-500">En papelera (células)</p><p className="text-xl font-semibold">{data.trash_counts?.cells ?? 0}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-gray-500">En papelera (miembros)</p><p className="text-xl font-semibold">{data.trash_counts?.members ?? 0}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-gray-500">En papelera (invitados)</p><p className="text-xl font-semibold">{data.trash_counts?.guests ?? 0}</p></CardContent></Card>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Detalle por célula</h3>
        <div className="overflow-x-auto rounded-lg border bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-3">Célula</th>
                <th className="text-left p-3">Ubicación</th>
                <th className="text-right p-3">Total</th>
                <th className="text-right p-3">Miembros</th>
                <th className="text-right p-3">Visitas</th>
                <th className="text-right p-3">Invit.</th>
                <th className="text-right p-3">Inactivos</th>
                <th className="text-right p-3">Cuidado</th>
                <th className="text-right p-3">Servicio</th>
              </tr>
            </thead>
            <tbody>
              {(data.cells_detail || []).map((row) => (
                <tr key={row.cell_id} className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium">{row.name}</td>
                  <td className="p-3 text-gray-600">{row.location || "—"}</td>
                  <td className="p-3 text-right">{row.total_members}</td>
                  <td className="p-3 text-right">{row.miembros}</td>
                  <td className="p-3 text-right">{row.visitas}</td>
                  <td className="p-3 text-right">{row.invitados_tipos}</td>
                  <td className="p-3 text-right">{row.inactive_members}</td>
                  <td className="p-3 text-right">{row.extra_care}</td>
                  <td className="p-3 text-right">{row.active_in_service}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============ Administradores de papelera (Pastor / Supervisor) ============
const ManageTrashAdmins = ({ onUserUpdated }) => {
  const [state, setState] = useState({ users: [], trash_admin_count: 0, trash_admin_max: 3 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/users");
      setState(res.data);
    } catch {
      setState((s) => ({ ...s, users: [] }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const grant = async (userId) => {
    try {
      await api.post(`/admin/trash-admins/${userId}`);
      await load();
      const me = await api.get("/auth/me");
      localStorage.setItem("user", JSON.stringify(me.data));
      onUserUpdated?.(me.data);
    } catch (e) {
      alert(e.response?.data?.detail || "No se pudo asignar");
    }
  };

  const revoke = async (userId) => {
    if (!window.confirm("¿Revocar el privilegio de administrador de papelera?")) return;
    try {
      await api.delete(`/admin/trash-admins/${userId}`);
      await load();
      const me = await api.get("/auth/me");
      localStorage.setItem("user", JSON.stringify(me.data));
      onUserUpdated?.(me.data);
    } catch (e) {
      alert(e.response?.data?.detail || "No se pudo revocar");
    }
  };

  if (loading) {
    return <p className="text-gray-500 text-sm py-4">Cargando usuarios…</p>;
  }

  return (
    <Card className="border-blue-100">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <UserCog className="w-5 h-5" />
          Administradores de papelera
        </CardTitle>
        <CardDescription>
          Máximo {state.trash_admin_max} pastores pueden vaciar la papelera o restaurar registros.
          Actualmente: {state.trash_admin_count} / {state.trash_admin_max}.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {state.users.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center justify-between gap-2 border rounded-lg p-3 bg-gray-50">
            <div>
              <p className="font-medium">{u.name}</p>
              <p className="text-xs text-gray-500">{u.username} · {u.role}</p>
            </div>
            <div className="flex gap-2">
              {u.is_trash_admin ? (
                <Badge className="bg-amber-600">Administrador papelera</Badge>
              ) : (
                <Badge variant="secondary">Sin privilegio papelera</Badge>
              )}
              {!u.is_trash_admin && state.trash_admin_count < state.trash_admin_max && (
                <Button size="sm" variant="outline" onClick={() => grant(u.id)}>
                  <Shield className="w-4 h-4 mr-1" /> Asignar
                </Button>
              )}
              {u.is_trash_admin && (
                <Button size="sm" variant="destructive" onClick={() => revoke(u.id)}>
                  Revocar
                </Button>
              )}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// ============ PAPELERA GLOBAL ============
const TrashBin = ({ cells, members, onRestored, canPermanentDelete }) => {
  const [trash, setTrash] = useState({ cells: [], members: [], guests: [] });
  const [loading, setLoading] = useState(true);
  const [permanentTarget, setPermanentTarget] = useState(null);
  const [permanentSubmitting, setPermanentSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/trash");
      setTrash(res.data);
    } catch (e) {
      if (e.response?.status === 403) {
        setTrash({ cells: [], members: [], guests: [] });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const cellName = (id) => cells.find((c) => (c.id || c._id) === id)?.name || id;
  const memberName = (id) => members.find((m) => (m.id || m._id) === id)?.name || id;

  const restore = async (collection, documentId) => {
    try {
      await api.post("/trash/restore", { collection, document_id: documentId });
      await load();
      onRestored?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Error al restaurar");
    }
  };

  const requestPermanent = (collection, documentId, label) => {
    if (!window.confirm(`¿Eliminar DEFINITIVAMENTE ${label}? Esta acción no se puede deshacer.`)) return;
    setPermanentTarget({ collection, documentId, label });
  };

  const handlePermanentConfirm = async (password) => {
    if (!permanentTarget) return;
    const { collection, documentId } = permanentTarget;
    setPermanentSubmitting(true);
    try {
      await api.delete(`/trash/permanent/${collection}/${documentId}`, { data: { password } });
      setPermanentTarget(null);
      await load();
    } catch (e) {
      alert(formatApiError(e) || "Error al eliminar");
    } finally {
      setPermanentSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const RowActions = ({ collection, doc, title }) => (
    <div className="flex gap-2 shrink-0">
      <Button size="sm" variant="outline" onClick={() => restore(collection, doc._id || doc.id)}>
        <ArchiveRestore className="w-4 h-4 mr-1" /> Restablecer
      </Button>
      {canPermanentDelete ? (
        <Button size="sm" variant="destructive" onClick={() => requestPermanent(collection, doc._id || doc.id, title)}>
          <Trash2 className="w-4 h-4 mr-1" /> Eliminar
        </Button>
      ) : null}
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Papelera</h2>
        <p className="text-gray-600 text-sm mt-1">
          Restablecer registros desde la papelera global. La eliminación definitiva requiere contraseña y solo pastor o administrador.
        </p>
      </div>

      <Card>
        <CardHeader><CardTitle>Células</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(trash.cells || []).length === 0 ? (
            <p className="text-gray-500 text-sm">No hay células en la papelera.</p>
          ) : (
            trash.cells.map((doc) => (
              <div key={doc._id} className="flex flex-wrap items-center justify-between gap-2 border rounded p-3">
                <span className="font-medium">{doc.name}</span>
                <RowActions collection="cells" doc={doc} title={`la célula «${doc.name}»`} />
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Miembros</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(trash.members || []).length === 0 ? (
            <p className="text-gray-500 text-sm">No hay miembros en la papelera.</p>
          ) : (
            trash.members.map((doc) => (
              <div key={doc._id} className="flex flex-wrap items-center justify-between gap-2 border rounded p-3">
                <div>
                  <span className="font-medium">{doc.name}</span>
                  <span className="text-gray-500 text-sm ml-2">· {cellName(doc.cell_id)}</span>
                </div>
                <RowActions collection="members" doc={doc} title={`a ${doc.name}`} />
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Invitados</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(trash.guests || []).length === 0 ? (
            <p className="text-gray-500 text-sm">No hay invitados en la papelera.</p>
          ) : (
            trash.guests.map((doc) => (
              <div key={doc._id} className="flex flex-wrap items-center justify-between gap-2 border rounded p-3">
                <div>
                  <span className="font-medium">{doc.name}</span>
                  <span className="text-gray-500 text-sm ml-2">
                    · {cellName(doc.cell_id)} · responsable: {memberName(doc.responsible_member_id)}
                  </span>
                </div>
                <RowActions collection="guests" doc={doc} title={`al invitado «${doc.name}»`} />
              </div>
            ))
          )}
        </CardContent>
      </Card>
      <TrashPermanentPasswordDialog
        open={Boolean(permanentTarget)}
        title="Eliminar definitivamente"
        description={
          permanentTarget
            ? `Confirme con contraseña la eliminación permanente de ${permanentTarget.label}.`
            : ""
        }
        onClose={() => !permanentSubmitting && setPermanentTarget(null)}
        onConfirm={handlePermanentConfirm}
        submitting={permanentSubmitting}
      />
    </div>
  );
};

// ============ MAIN APP COMPONENT ============
function App() {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState({});
  const [cells, setCells] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgChartOpen, setOrgChartOpen] = useState(false);

  const cellEditableIds = useMemo(
    () => cellEditableIdSetForUser(user, cells, members),
    [user, cells, members],
  );

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => {
        setUser(res.data);
        localStorage.setItem("user", JSON.stringify(res.data));
      })
      .catch(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        clearAdminToolsUnlock();
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const dataRefreshTabs = useMemo(
    () =>
      new Set([
        "dashboard",
        "cells",
        "members",
        "cell-attendance",
        "cell-quick-stats",
      ]),
    []
  );

  useEffect(() => {
    if (!user || !dataRefreshTabs.has(activeTab)) return;
    fetchData();
  }, [activeTab, user]);

  useEffect(() => {
    if (!user) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchData();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    if (activeTab === "reports" && !isPastorOrSupervisor(user)) setActiveTab("dashboard");
    if (activeTab === "cell-attendance" && !isPastorSupervisorOrCellStaff(user)) setActiveTab("dashboard");
    if (activeTab === "cell-quick-stats" && !isPastorSupervisorOrCellStaff(user)) setActiveTab("dashboard");
    if (activeTab === "trash" && !canAccessGlobalTrash(user)) setActiveTab("dashboard");
    if (activeTab === "admin-tools" && !canAccessAdminTools(user)) setActiveTab("dashboard");
  }, [user, activeTab]);

  const fetchData = async () => {
    try {
      const [statsRes, cellsRes, membersRes] = await Promise.all([
        api.get("/stats/dashboard"),
        api.get("/cells"),
        api.get("/members")
      ]);
      setStats(statsRes.data);
      setCells((cellsRes.data || []).map(normalizeCellFromApi));
      setMembers((membersRes.data || []).map(normalizeMemberFromApi));
    } catch (err) {
      console.error("Error fetching data:", err);
      if (err.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    clearAdminToolsUnlock();
    setUser(null);
    setStats({});
    setCells([]);
    setMembers([]);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-blue-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold">ICJEME</h1>
                <p className="text-blue-100 text-sm">Sistema de Gestión de Células</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <span className="text-blue-100 block text-sm">Hola, {user.name}</span>
                <span className="text-blue-200 text-xs">
                  {roleLabel(user.role)}
                  {user.role === ROLE_PASTOR && user.is_trash_admin ? " · Admin papelera" : ""}
                </span>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="text-white hover:bg-blue-700"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Salir
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-1 overflow-x-auto">
            <button
              onClick={() => setActiveTab("dashboard")}
              className={`px-4 py-3 flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === "dashboard" 
                  ? "border-blue-600 text-blue-600" 
                  : "border-transparent text-gray-600 hover:text-blue-600"
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab("cells")}
              className={`px-4 py-3 flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === "cells" 
                  ? "border-blue-600 text-blue-600" 
                  : "border-transparent text-gray-600 hover:text-blue-600"
              }`}
            >
              <Home className="w-4 h-4" />
              Células
            </button>
            <button
              onClick={() => setActiveTab("members")}
              className={`px-4 py-3 flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === "members" 
                  ? "border-blue-600 text-blue-600" 
                  : "border-transparent text-gray-600 hover:text-blue-600"
              }`}
            >
              <Users className="w-4 h-4" />
              Miembros
            </button>
            {isPastorOrSupervisor(user) && (
              <button
                onClick={() => setActiveTab("reports")}
                className={`px-4 py-3 flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === "reports"
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-600 hover:text-blue-600"
                }`}
              >
                <FileText className="w-4 h-4" />
                Informes
              </button>
            )}
            {isPastorSupervisorOrCellStaff(user) && (
              <button
                onClick={() => setActiveTab("cell-attendance")}
                className={`px-4 py-3 flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === "cell-attendance"
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-600 hover:text-blue-600"
                }`}
              >
                <CalendarCheck className="w-4 h-4" />
                Asistencia por Célula
              </button>
            )}
            {isPastorSupervisorOrCellStaff(user) && (
              <button
                type="button"
                onClick={() => setActiveTab("cell-quick-stats")}
                className={`px-4 py-3 flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === "cell-quick-stats"
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-600 hover:text-blue-600"
                }`}
              >
                <TrendingUp className="w-4 h-4" />
                Estadística Rápida
              </button>
            )}
            {canAccessGlobalTrash(user) && (
              <button
                onClick={() => setActiveTab("trash")}
                className={`px-4 py-3 flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === "trash"
                    ? "border-amber-600 text-amber-700"
                    : "border-transparent text-gray-600 hover:text-amber-700"
                }`}
              >
                <Trash2 className="w-4 h-4" />
                Papelera
              </button>
            )}
            {canAccessAdminTools(user) && (
              <button
                type="button"
                onClick={() => setActiveTab("admin-tools")}
                className={`px-4 py-3 flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === "admin-tools"
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-600 hover:text-blue-600"
                }`}
              >
                <Wrench className="w-4 h-4" />
                Herramientas Administrativas
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {activeTab === "dashboard" && (
          <Dashboard stats={stats} members={members} cells={cells} />
        )}
        {activeTab === "cells" && (
          <div className="space-y-2">
            <DataIoToolbar
              resource="cells"
              canImport={canManageOrg(user)}
              canExport={canManageOrg(user)}
              onCompleted={fetchData}
              onOpenOrganizationChart={canManageOrg(user) ? () => setOrgChartOpen(true) : undefined}
            />
            <Cells
              cells={cells}
              members={members}
              onRefresh={fetchData}
              canCreateCell={canManageOrg(user)}
              canDeleteCell={canManageOrg(user)}
              canViewCellsTrash={canViewCellsTrash(user)}
              canRestoreCellTrash={canRestoreFromTrash(user)}
              canPermanentDeleteCellTrash={canPermanentDeleteFromTrash(user)}
              cellEditableIds={cellEditableIds}
            />
          </div>
        )}
        {activeTab === "members" && (
          <div className="space-y-2">
            <DataIoToolbar
              resource="members"
              canImport={canWriteMembers(user)}
              disableExport
              onCompleted={fetchData}
            />
            <MembersPanel
              members={members}
              cells={cells}
              onRefresh={fetchData}
              canManage={canWriteMembers(user)}
              canTrashMembers={canViewCellsTrash(user)}
              canPermanentDeleteMembers={canPermanentDeleteFromTrash(user)}
            />
          </div>
        )}
        {activeTab === "reports" && isPastorOrSupervisor(user) && (
          <div className="space-y-10">
            <ReportsPastoral />
            {user?.role === ROLE_PASTOR && (
              <ManageTrashAdmins onUserUpdated={(u) => setUser(u)} />
            )}
          </div>
        )}
        {activeTab === "cell-attendance" && isPastorSupervisorOrCellStaff(user) && (
          <CellMeetingAttendanceScreen
            api={api}
            cells={cells}
            members={members}
            formatApiError={formatApiError}
          />
        )}
        {activeTab === "cell-quick-stats" && isPastorSupervisorOrCellStaff(user) && (
          <CellQuickWeeklyStatsScreen
            api={api}
            cells={cells}
            formatApiError={formatApiError}
            hasFullAccess={isFullAccess(user)}
            userRole={user?.role}
          />
        )}
        {activeTab === "trash" && canAccessGlobalTrash(user) && (
          <TrashBin
            cells={cells}
            members={members}
            onRestored={fetchData}
            canPermanentDelete={canPermanentDeleteFromTrash(user)}
          />
        )}
        {activeTab === "admin-tools" && canAccessAdminTools(user) && (
          <AdminToolsPanel onCompleted={fetchData} onExit={() => setActiveTab("dashboard")} />
        )}
      </main>
      <CellOrganizationChart api={api} open={orgChartOpen} onClose={() => setOrgChartOpen(false)} />
    </div>
  );
}

export default App;
